import jsonServerProvider from "ra-data-json-server";
import { fetchUtils, HttpError, Options, DataProvider } from "react-admin";
import { API_URL } from "./constants";
import { getAccessToken } from "./utils/storage";

export const httpClient = (url: string, options: Options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
    options.credentials = "omit";
  }
  const token = getAccessToken();
  if (token) {
    (options.headers as Headers).set("Authorization", `Bearer ${token}`);
  }

  return fetchUtils.fetchJson(url, options).catch((error: HttpError) => {
    if (error.body?.statusCode === 400) {
      const message = Array.isArray(error.body.message)
        ? error.body.message.join("\n")
        : error.body.message;
      throw new Error(message);
    }
    throw error;
  });
};

export { API_URL };

export const APP_VERSION =
  import.meta.env.VITE_APP_VERSION ||
  import.meta.env.REACT_APP_VERSION ||
  "0.0.0";

const baseDataProvider = jsonServerProvider(API_URL, httpClient);

// Helper function to convert string booleans to actual booleans
const transformBooleanStrings = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === "string" && (obj === "true" || obj === "false")) {
    return obj === "true";
  }
  if (Array.isArray(obj)) {
    return obj.map(transformBooleanStrings);
  }
  if (typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = transformBooleanStrings(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};

// Helper function to transform data before sending to API (if needed)
const transformForAPI = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(transformForAPI);
  }
  if (typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = transformForAPI(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};

export const dataProvider: DataProvider = {
  ...baseDataProvider,
  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/${resource}/${id}`, { method: "DELETE" }),
      ),
    );
    return { data: params.ids };
  },
  getList: async (resource, params) => {
    const result = await baseDataProvider.getList(resource, params);
    return {
      ...result,
      data: result.data.map(transformBooleanStrings),
    };
  },
  getOne: async (resource, params) => {
    const result = await baseDataProvider.getOne(resource, params);
    return {
      ...result,
      data: transformBooleanStrings(result.data),
    };
  },
  getMany: async (resource, params) => {
    const result = await baseDataProvider.getMany(resource, params);
    return {
      ...result,
      data: result.data.map(transformBooleanStrings),
    };
  },
  getManyReference: async (resource, params) => {
    const result = await baseDataProvider.getManyReference(resource, params);
    return {
      ...result,
      data: result.data.map(transformBooleanStrings),
    };
  },
};
